import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, MapPin, DollarSign, Clock, ExternalLink, Building2, Calendar, Users, FileText, CheckCircle, AlertCircle, X, Mail, Phone, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import jobAPI from '../services/jobAPI';

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedJobs, setSavedJobs] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [message, setMessage] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationData, setApplicationData] = useState({
    coverLetter: '',
    resume: null,
    resumeFileName: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Mock job data - Fallback when API is not configured or fails
  const mockJobs = [
    {
      id: 1,
      title: 'Senior Software Engineer',
      company: 'Tech Innovations Inc.',
      location: 'San Francisco, CA',
      salary: '$120,000 - $150,000',
      type: 'Full-time',
      posted: '2 days ago',
      description: 'We are seeking a highly skilled Senior Software Engineer to join our dynamic team. You will be responsible for designing, developing, and maintaining scalable web applications using modern technologies.',
      requirements: [
        'Bachelor\'s degree in Computer Science or related field',
        '5+ years of experience in software development',
        'Proficiency in JavaScript, React, and Node.js',
        'Experience with cloud platforms (AWS, Azure, or GCP)',
        'Strong problem-solving and communication skills'
      ],
      responsibilities: [
        'Design and develop scalable web applications',
        'Collaborate with cross-functional teams',
        'Write clean, maintainable code',
        'Participate in code reviews',
        'Mentor junior developers'
      ],
      benefits: [
        'Competitive salary and equity package',
        'Comprehensive health insurance',
        '401(k) matching',
        'Flexible work arrangements',
        'Professional development opportunities'
      ],
      link: '#'
    },
    {
      id: 2,
      title: 'Frontend Developer',
      company: 'Digital Solutions',
      location: 'Remote',
      salary: '$90,000 - $110,000',
      type: 'Full-time',
      posted: '1 day ago',
      description: 'Join our team as a Frontend Developer and help build beautiful, user-friendly web applications. We value creativity, attention to detail, and a passion for creating exceptional user experiences.',
      requirements: [
        '3+ years of frontend development experience',
        'Strong knowledge of HTML, CSS, and JavaScript',
        'Experience with React or Vue.js',
        'Understanding of responsive design principles',
        'Portfolio demonstrating frontend skills'
      ],
      responsibilities: [
        'Build responsive web interfaces',
        'Implement UI/UX designs',
        'Optimize application performance',
        'Collaborate with designers and backend developers',
        'Ensure cross-browser compatibility'
      ],
      benefits: [
        'Remote work flexibility',
        'Health and dental insurance',
        'Unlimited PTO',
        'Learning and development budget',
        'Home office stipend'
      ],
      link: '#'
    },
    {
      id: 3,
      title: 'Product Manager',
      company: 'StartupHub',
      location: 'New York, NY',
      salary: '$100,000 - $130,000',
      type: 'Full-time',
      posted: '3 days ago',
      description: 'We are looking for an experienced Product Manager to drive product strategy and execution. You will work closely with engineering, design, and business teams to deliver innovative products.',
      requirements: [
        'Bachelor\'s degree in Business, Engineering, or related field',
        '3+ years of product management experience',
        'Strong analytical and strategic thinking skills',
        'Experience with agile methodologies',
        'Excellent communication and leadership skills'
      ],
      responsibilities: [
        'Define product vision and roadmap',
        'Gather and prioritize product requirements',
        'Work with engineering teams on feature development',
        'Analyze user feedback and metrics',
        'Coordinate product launches'
      ],
      benefits: [
        'Competitive compensation package',
        'Stock options',
        'Health insurance',
        'Flexible work schedule',
        'Team building events'
      ],
      link: '#'
    },
    {
      id: 4,
      title: 'UX Designer',
      company: 'Creative Agency',
      location: 'Los Angeles, CA',
      salary: '$85,000 - $105,000',
      type: 'Full-time',
      posted: '5 days ago',
      description: 'We are seeking a talented UX Designer to create intuitive and engaging user experiences. You will work on a variety of projects for clients across different industries.',
      requirements: [
        'Portfolio demonstrating UX design skills',
        'Experience with design tools (Figma, Sketch, Adobe XD)',
        'Understanding of user research methods',
        'Strong visual design skills',
        'Ability to work in a fast-paced environment'
      ],
      responsibilities: [
        'Conduct user research and usability testing',
        'Create wireframes and prototypes',
        'Design user interfaces',
        'Collaborate with clients and stakeholders',
        'Present design concepts and iterate based on feedback'
      ],
      benefits: [
        'Creative and collaborative work environment',
        'Health and vision insurance',
        'Professional development opportunities',
        'Flexible PTO',
        'Design tool subscriptions'
      ],
      link: '#'
    },
    {
      id: 5,
      title: 'Data Scientist',
      company: 'Analytics Corp',
      location: 'Boston, MA',
      salary: '$110,000 - $140,000',
      type: 'Full-time',
      posted: '1 week ago',
      description: 'Join our data science team to extract insights from complex datasets and build predictive models. You will work on challenging problems and have the opportunity to make a significant impact.',
      requirements: [
        'Master\'s degree in Statistics, Mathematics, or related field',
        '3+ years of experience in data science',
        'Proficiency in Python or R',
        'Experience with machine learning frameworks',
        'Strong statistical analysis skills'
      ],
      responsibilities: [
        'Analyze large datasets',
        'Build and deploy machine learning models',
        'Create data visualizations',
        'Present findings to stakeholders',
        'Collaborate with engineering teams'
      ],
      benefits: [
        'Competitive salary',
        'Comprehensive benefits package',
        'Research and conference opportunities',
        'Flexible work arrangements',
        '401(k) with company match'
      ],
      link: '#'
    },
    {
      id: 6,
      title: 'Marketing Manager',
      company: 'Growth Partners',
      location: 'Chicago, IL',
      salary: '$75,000 - $95,000',
      type: 'Full-time',
      posted: '4 days ago',
      description: 'We are looking for a Marketing Manager to develop and execute marketing strategies that drive growth and brand awareness. You will lead marketing campaigns across multiple channels.',
      requirements: [
        'Bachelor\'s degree in Marketing or related field',
        '4+ years of marketing experience',
        'Experience with digital marketing tools',
        'Strong analytical skills',
        'Excellent written and verbal communication'
      ],
      responsibilities: [
        'Develop marketing strategies and campaigns',
        'Manage marketing budget',
        'Analyze campaign performance',
        'Coordinate with creative and content teams',
        'Build brand awareness'
      ],
      benefits: [
        'Competitive salary',
        'Health insurance',
        'Marketing budget for campaigns',
        'Professional development',
        'Team outings and events'
      ],
      link: '#'
    }
  ];

  // Fetch job from API (with mock fallback)
  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      try {
        const fetchedJob = await jobAPI.getJobDetails(id);
        if (fetchedJob) {
          setJob(fetchedJob);
          // Check if job is already saved
          if (isAuthenticated) {
            const saved = JSON.parse(localStorage.getItem('savedJobs') || '[]');
            setIsSaved(saved.some(j => j.id === fetchedJob.id || j.jobId === fetchedJob.jobId));
          }
        } else {
          // If not found in API, try mock data as fallback
          const mockJob = mockJobs.find(j => j.id === parseInt(id));
          if (mockJob) {
            setJob(mockJob);
            if (isAuthenticated) {
              const saved = JSON.parse(localStorage.getItem('savedJobs') || '[]');
              setIsSaved(saved.some(j => j.id === mockJob.id));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching job:', error);
        // Fallback to mock data
        const mockJob = mockJobs.find(j => j.id === parseInt(id));
        if (mockJob) {
          setJob(mockJob);
          if (isAuthenticated) {
            const saved = JSON.parse(localStorage.getItem('savedJobs') || '[]');
            setIsSaved(saved.some(j => j.id === mockJob.id));
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id, isAuthenticated]);

  // Load saved jobs from localStorage
  useEffect(() => {
    if (isAuthenticated) {
      const saved = JSON.parse(localStorage.getItem('savedJobs') || '[]');
      setSavedJobs(saved);
    }
  }, [isAuthenticated]);


  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleApplyNow = () => {
    if (!isAuthenticated) {
      showMessage('Please log in to apply for jobs', 'error');
      setTimeout(() => {
        navigate('/login', { state: { from: `/jobs/${id}` } });
      }, 1000);
      return;
    }

    if (!job) {
      showMessage('Job information not available. Please try again.', 'error');
      return;
    }

    // If job has an external link (and it's not a placeholder), open it
    if (job.link && job.link !== '#' && job.link.startsWith('http')) {
      // Open external application link in new tab
      window.open(job.link, '_blank', 'noopener,noreferrer');
      showMessage('Opening application form in a new tab...', 'success');
      return;
    }

    // Open application modal for internal application
    setShowApplicationModal(true);
  };

  const handleCloseModal = () => {
    setShowApplicationModal(false);
    setApplicationData({
      coverLetter: '',
      resume: null,
      resumeFileName: ''
    });
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5 MB limit
        showMessage('Resume file size exceeds 5 MB limit. Please use a smaller file.', 'error');
        return;
      }
      setApplicationData({
        ...applicationData,
        resume: file,
        resumeFileName: file.name
      });
    }
  };

  const handleSubmitApplication = async () => {
    if (!applicationData.coverLetter.trim()) {
      showMessage('Please provide a cover letter', 'error');
      return;
    }

    setSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      showMessage('Application submitted successfully! The employer will review your application.', 'success');
      handleCloseModal();
      
      // In production, you would:
      // 1. Upload resume to server
      // 2. Submit application data to API
      // 3. Send confirmation email
      // 4. Track application status
    }, 1500);
  };

  const handleSaveJob = () => {
    if (!isAuthenticated) {
      showMessage('Please log in to save jobs', 'error');
      setTimeout(() => {
        navigate('/login', { state: { from: `/jobs/${id}` } });
      }, 1000);
      return;
    }

    if (!job) return;

    const saved = JSON.parse(localStorage.getItem('savedJobs') || '[]');
    
    if (isSaved) {
      // Remove from saved jobs
      const updated = saved.filter(j => j.id !== job.id);
      localStorage.setItem('savedJobs', JSON.stringify(updated));
      setSavedJobs(updated);
      setIsSaved(false);
      showMessage('Job removed from saved jobs', 'success');
    } else {
      // Add to saved jobs
      const updated = [...saved, { ...job, savedAt: new Date().toISOString() }];
      localStorage.setItem('savedJobs', JSON.stringify(updated));
      setSavedJobs(updated);
      setIsSaved(true);
      showMessage('Job saved successfully!', 'success');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h2>
          <Link to="/jobs" className="btn btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Job Listings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-custom py-8">
        {/* Message Banner */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Back Button */}
        <Link
          to="/jobs"
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Job Listings
        </Link>

        {/* Job Header */}
        <div className="card mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-gray-900 mb-3">{job.title}</h1>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2 text-primary-600">
                  <Building2 className="w-5 h-5" />
                  <span className="text-lg font-semibold">{job.company}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  <span>{job.salary}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{job.type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>Posted {job.posted}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <button 
              onClick={handleApplyNow}
              className="btn btn-primary inline-flex items-center space-x-2"
            >
              <span>Apply Now</span>
              <ExternalLink className="w-4 h-4" />
            </button>
            <button 
              onClick={handleSaveJob}
              className={`btn ${isSaved ? 'btn-secondary' : 'btn-outline'}`}
            >
              {isSaved ? 'Saved' : 'Save Job'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Description */}
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary-600" />
                Job Description
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">{job.description}</p>

              {/* Responsibilities */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Key Responsibilities</h3>
                <ul className="space-y-2">
                  {job.responsibilities.map((responsibility, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-primary-600 mt-1">•</span>
                      <span>{responsibility}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Requirements */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Requirements</h3>
                <ul className="space-y-2">
                  {job.requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-primary-600 mt-1">•</span>
                      <span>{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Job Summary */}
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Job Summary</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Company</p>
                  <p className="font-medium text-gray-900">{job.company}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Location</p>
                  <p className="font-medium text-gray-900">{job.location}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Salary</p>
                  <p className="font-medium text-gray-900">{job.salary}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Job Type</p>
                  <p className="font-medium text-gray-900">{job.type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Posted</p>
                  <p className="font-medium text-gray-900">{job.posted}</p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary-600" />
                Benefits
              </h3>
              <ul className="space-y-2">
                {job.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-gray-700">
                    <span className="text-primary-600 mt-1">✓</span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Apply Button */}
            <div className="card bg-primary-50 border-primary-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ready to Apply?</h3>
              {isAuthenticated ? (
                <>
                  <p className="text-gray-600 mb-4 text-sm">
                    Click the button below to apply for this position through our application portal.
                  </p>
                  <button 
                    onClick={handleApplyNow}
                    className="btn btn-primary w-full inline-flex items-center justify-center space-x-2"
                  >
                    <span>Apply Now</span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-4 text-sm">
                    Please log in to apply for this position. You'll need a profile to submit your application.
                  </p>
                  <Link 
                    to="/login" 
                    state={{ from: `/jobs/${id}` }}
                    className="btn btn-primary w-full"
                  >
                    Log In to Apply
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Apply for {job.title}</h2>
                  <p className="text-gray-600 mt-1">{job.company} • {job.location}</p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Application Form */}
              <div className="space-y-6">
                {/* Applicant Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Your Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mail className="w-4 h-4" />
                      <span>{user?.email || 'Not available'}</span>
                    </div>
                    {user?.name && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <Users className="w-4 h-4" />
                        <span>{user.name}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Your profile information will be included with your application.
                  </p>
                </div>

                {/* Cover Letter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Letter <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={applicationData.coverLetter}
                    onChange={(e) => setApplicationData({ ...applicationData, coverLetter: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    rows="8"
                    placeholder="Write a compelling cover letter explaining why you're a great fit for this position..."
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {applicationData.coverLetter.length} characters
                  </p>
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resume (PDF, DOC, DOCX)
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {applicationData.resumeFileName ? (
                      <div className="space-y-2">
                        <FileText className="w-12 h-12 text-primary-600 mx-auto" />
                        <p className="text-sm font-medium text-gray-900">{applicationData.resumeFileName}</p>
                        <button
                          type="button"
                          onClick={() => setApplicationData({ ...applicationData, resume: null, resumeFileName: '' })}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleResumeUpload}
                            className="hidden"
                          />
                          <span className="btn btn-outline inline-flex items-center space-x-2">
                            <Upload className="w-4 h-4" />
                            <span>Upload Resume</span>
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 mt-2">Max file size: 5 MB</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCloseModal}
                    className="btn btn-outline flex-1"
                    disabled={submitting}
                    type="button"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitApplication}
                    className="btn btn-primary flex-1 inline-flex items-center justify-center space-x-2"
                    disabled={submitting || !applicationData.coverLetter.trim()}
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Application</span>
                        <CheckCircle className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetailPage;
